import { ErrorMessage } from "@components/atoms/ErrorMessage"
import { Button } from "@components/controls/Button"
import { TextInput } from "@components/controls/TextInput"
import { LoadingResults } from "@components/layout/LoadingResults"
import { IconUnlock } from "@components/svg/Icons"
import { useCouponsEnabled } from "@features/events/components/CouponEnabler"
import { useEffect, useMemo, useRef, useState } from "react"
import { CartContentFragment, ProductContentSliceFragment } from "../../../types.graphql"
import { CartController } from "../hooks/useCartController"

type Props = {
  cart: CartContentFragment
  controller?: CartController
}

export function CartSummary(props: Props) {
  const { cart, controller } = props

  const enableCoupons = useCouponsEnabled()

  const items = useMemo(() => {
    const rows = []
    let lastHeading = ""
    if (cart.contents?.nodes) {
      for (const item of cart.contents.nodes) {
        let name = item.product?.node.name!
        if (item.product?.node.__typename === "VariableProduct") {
          if (name !== lastHeading) {
            lastHeading = name
            rows.push(
              <div key={"heading" + item.key} className="flex justify-between items-center py-3 border-b type-title-xs-medium border-grey-light">
                <span dangerouslySetInnerHTML={{ __html: name }} />
              </div>
            )
          }
          name = item.variation?.node.description!

          rows.push(
            <div className="flex justify-between items-center py-2 border-b border-grey-light" key={item.key}>
              <div className="stack-y-1">
                {name && <div className="type-title-xs-roman">{<span dangerouslySetInnerHTML={{ __html: name }} />}</div>}
                <div className="type-body-s-roman text-grey-mid">{item.quantity == 1 ? "1 ticket" : `${item.quantity} tickets`}</div>
              </div>
              <div className="type-body-s-roman pl-2">{item.subtotal}</div>
            </div>
          )
        } else {
          rows.push(
            <div className="flex justify-between items-center py-4 border-b border-grey-light" key={item.key}>
              <div className="stack-y-1">
                {name && <div className="type-title-xs-medium">{name}</div>}
                {item.product?.node.__typename === "SubscriptionProduct" && (
                  <div className="type-body-s-roman text-grey-dark">{describeProductPrice(item.product?.node!) ?? item.subtotal}</div>
                )}
              </div>
              {item.product?.node.__typename === "SubscriptionProduct" ? (
                <div className="type-body-s-roman hidden @[300px]:block">{item.subtotal}</div>
              ) : (
                <div className="type-body-s-roman pl-2">{item.subtotal}</div>
              )}
            </div>
          )
        }
      }
    }
    return rows
  }, [cart.contents?.nodes])

  return (
    <div className="@container">
      {/* Products */}

      <>{items}</>

      {/* Coupons */}
      {!!enableCoupons && (
        <div className="stack-y-2 py-4 border-b border-grey-light">
          {cart.appliedCoupons?.map((coupon, i) => (
            <div key={i} className="flex justify-between items-center mb-2">
              <div className="type-body-s-roman text-grey-dark">Code: {coupon?.code.toUpperCase()}</div>
              <div className="type-title-xs-roman">{coupon?.discountAmount}</div>
            </div>
          ))}
          {controller && (
            <div className="self-start">
              <AddCoupon controller={controller} />
            </div>
          )}
        </div>
      )}

      {/* Subtotal */}
      <div className="border-b border-grey-light py-2 flex justify-between">
        <div className="type-body-s-roman">Subtotal</div>
        <div className="type-body-s-roman">
          <div>{cart.subtotal}</div>
        </div>
      </div>

      {/* Taxes */}
      {!!cart.totalTax && (
        <div className="border-b border-grey-light py-2 flex justify-between">
          <div className="type-body-s-roman">GST (10%)</div>
          <div className="type-body-s-roman">
            <div>{cart.totalTax}</div>
          </div>
        </div>
      )}

      {/* Subtotal */}
      <div className="border-b border-grey-light py-4 flex justify-between">
        <div className="type-title-xs-medium">Total due</div>
        <div className="text-right">
          <div className="type-title-xs-medium">{cart.total}</div>
        </div>
      </div>
    </div>
  )
}

function describeProductPrice(product: ProductContentSliceFragment) {
  if ("price" in product) {
    return product.price
  }
  return null
}

function AddCoupon({ controller }: { controller: CartController }) {
  const [active, setActive] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const [state, setState] = useState<null | { working: boolean; error: string }>(null)

  useEffect(() => {
    if (!active) {
      setState(null)
    }
  }, [active])

  if (active) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          const value = input.current!.value
          if (!value) return
          setState({ working: true, error: "" })
          controller
            .addCoupon(value)
            .then(() => {
              setState({ working: false, error: "" })
              setActive(false)
            })
            .catch((e) => {
              setState({ working: false, error: e.message })
            })
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setActive(false)
        }}
        className="stack-y-1"
      >
        {state?.error ? <ErrorMessage className="type-body-s-roman">{state.error}</ErrorMessage> : null}
        <TextInput ref={input} autoComplete="off" placeholder="Enter coupon code" autoFocus disabled={state?.working || controller.pending} onInput={() => setState(null)} />
        <div className="stack-x-2">
          <Button type="submit" rightIcon={<IconUnlock />} size="chip" variant="outline" loading={state?.working} disabled={controller.pending}>
            Apply
          </Button>
          <Button type="button" onClick={() => setActive(false)} size="chip" variant="light" disabled={state?.working}>
            Cancel
          </Button>
        </div>
      </form>
    )
  } else {
    return (
      <div className="flex">
        <Button size="chip" variant="light" onClick={() => setActive(true)}>
          Add a coupon
        </Button>
      </div>
    )
  }
}

function EmptyCart() {
  return <div>Empty cart</div>
}

export function CartControllerSummary(props: { controller: CartController }) {
  const cart = props.controller

  if (cart.data?.contents?.nodes.length) {
    return <CartSummary cart={cart?.data} controller={cart} />
  } else if (cart.pending) {
    return <LoadingResults />
  } else {
    return <EmptyCart />
  }
}
